import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, LogOut, ShieldCheck, PhoneCall, LayoutDashboard, ClipboardCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import MegaMenu from "@/components/MegaMenu";
import BrandLogo from "@/components/BrandLogo";
import StudyAbroadMenu from "@/components/StudyAbroadMenu";
import SearchBox from "@/components/SearchBox";
import { domainPath } from "@/lib/domainLinks";
import CallbackModal from "@/components/CallbackModal";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";
import { useClientReady } from "@/hooks/useClientReady";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [domains, setDomains] = useState([]);
  const [hdr, setHdr] = useState(() => window.__TB_HEADER__ || null);
  const clientReady = useClientReady();

  useEffect(() => {
    api.get("/categories").then((r) => setDomains(r.data.categories)).catch(() => {});
    api.get("/website/sections/header").then((r) => setHdr(r.data)).catch(() => {});
  }, []);

  const navLinks = (hdr?.nav_links?.length ? hdr.nav_links : [
    { label: "Institutes", link: "/institutes", visible: true },
    { label: "Blog", link: "/blog", visible: true },
  ]).filter((l) => l.visible !== false && l.label);

  const initials = user && user.name ? user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() : "";

  return (
    <header className="sticky top-3 z-50 px-3 sm:px-5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-5 bg-white/95 backdrop-blur-md rounded-full shadow-lg shadow-slate-900/5 border border-slate-200/80">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
          <BrandLogo className="h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <MegaMenu label={hdr?.programs_label || "Programs"} link={hdr?.programs_link || "/courses"} onCallback={() => setCallbackOpen(true)} showArrow={hdr?.show_arrows ?? true} arrowStyle={hdr?.arrow_style || "chevron"} />
          {(hdr?.study_abroad_visible ?? false) && (
            <StudyAbroadMenu label={hdr?.study_abroad_label || "Study Abroad"} link={hdr?.study_abroad_link || "/courses"} onCallback={() => setCallbackOpen(true)} showArrow={hdr?.show_arrows ?? true} arrowStyle={hdr?.arrow_style || "chevron"} />
          )}
          {navLinks.map((l, i) => (
            <Link key={i} to={l.link} data-testid={`nav-static-link-${i}`} className="text-sm font-semibold text-slate-700 hover:text-rose-600 transition-colors">{l.label}</Link>
          ))}
        </nav>

        {(hdr?.show_search ?? false) && (
          <div className="flex-1 hidden md:block max-w-xs ml-auto">
            <SearchBox placeholder={hdr?.search_placeholder || "Search courses, institutes…"} />
          </div>
        )}

        <div className="hidden lg:flex items-center gap-3 ml-auto">
          {(hdr?.show_counselor ?? true) && (
            <button onClick={() => setCallbackOpen(true)} data-testid="nav-counselor-cta"
              className="flex items-center gap-2 text-sm font-bold text-rose-700 hover:text-rose-800 border border-rose-300 hover:border-rose-400 rounded-full px-4 py-2.5 transition-colors">
              <PhoneCall className="w-4 h-4" /> {hdr?.counselor_label || "Counselor call"}
            </button>
          )}
          {user ? (
            <>
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" aria-label="Account menu"><Avatar className="h-9 w-9"><AvatarFallback className="bg-rose-100 text-rose-700 font-bold text-sm">{initials}</AvatarFallback></Avatar></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 p-1 overflow-hidden rounded-2xl">
                <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/profile")}><LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard</DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-testing-link" onClick={() => navigate("/testing")}><ClipboardCheck className="w-4 h-4 mr-2" /> My Tests</DropdownMenuItem>
                {["admin", "counselor"].includes(user.role) && null}
                <DropdownMenuItem data-testid="nav-logout-button" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link"><Button variant="ghost" className="font-semibold">{hdr?.login_label || "Log in"}</Button></Link>
              <Link to="/signup" data-testid="nav-signup-cta"><Button className="bg-rose-600 hover:bg-rose-700 font-bold rounded-full px-6 uppercase text-xs tracking-wider">{hdr?.signup_label || "Sign up"}</Button></Link>
            </>
          )}
        </div>

        {clientReady ? <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2 ml-auto" data-testid="nav-mobile-menu-button" aria-label="Open navigation menu"><Menu className="w-6 h-6 text-slate-800" /></button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto">
            <div className="flex flex-col gap-1 mt-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-2 mb-1">Programs by domain</p>
              {domains.map((d) => (
                <Link key={d.slug} to={domainPath(d.slug)} data-testid={`mobile-domain-${d.slug}`} onClick={() => setOpen(false)}
                  className="px-2 py-2.5 rounded-lg text-sm font-semibold text-slate-800 hover:bg-rose-50 hover:text-rose-700">{d.name}</Link>
              ))}
              <div className="border-t my-3" />
              {/^https?:\/\//i.test(hdr?.programs_link || "") ? (
                <a href={hdr.programs_link} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="px-2 py-2.5 text-sm font-semibold text-slate-800" data-testid="mobile-all-courses">All programs</a>
              ) : (
                <Link to={hdr?.programs_link || "/courses"} onClick={() => setOpen(false)} className="px-2 py-2.5 text-sm font-semibold text-slate-800" data-testid="mobile-all-courses">All programs</Link>
              )}
              {(hdr?.study_abroad_visible ?? true) && (
                /^https?:\/\//i.test(hdr?.study_abroad_link || "") ? (
                  <a href={hdr.study_abroad_link} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="px-2 py-2.5 text-sm font-semibold text-slate-800" data-testid="mobile-destinations">{hdr?.study_abroad_label || "Study Abroad"}</a>
                ) : (
                  <Link to={hdr?.study_abroad_link || "/courses"} onClick={() => setOpen(false)} className="px-2 py-2.5 text-sm font-semibold text-slate-800" data-testid="mobile-destinations">{hdr?.study_abroad_label || "Study Abroad"}</Link>
                )
              )}
              {navLinks.map((l, i) => (
                <Link key={i} to={l.link} onClick={() => setOpen(false)} className="px-2 py-2.5 text-sm font-semibold text-slate-800" data-testid={`mobile-static-link-${i}`}>{l.label}</Link>
              ))}
              <div className="border-t my-3" />
              <Button className="bg-rose-600 hover:bg-rose-700 rounded-xl mx-2" onClick={() => { setOpen(false); setCallbackOpen(true); }} data-testid="mobile-counselor-cta">
                <PhoneCall className="w-4 h-4 mr-2" /> {hdr?.counselor_label || "Request counselor call"}
              </Button>
              <div className="mt-4 flex flex-col gap-3 px-2">
                {user ? (
                  <>
                    <Link to="/profile" data-testid="nav-profile-link-mobile" onClick={() => setOpen(false)} className="font-semibold text-slate-800">My profile</Link>
                    {["admin", "counselor"].includes(user.role) && null}
                    <button data-testid="nav-logout-button-mobile" onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-left font-semibold text-rose-600">Log out</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" data-testid="nav-login-link-mobile" onClick={() => setOpen(false)} className="font-semibold text-slate-800">{hdr?.login_label || "Log in"}</Link>
                    <Link to="/signup" data-testid="nav-signup-cta-mobile" onClick={() => setOpen(false)}><Button className="w-full bg-rose-600 hover:bg-rose-700 rounded-xl">{hdr?.signup_label || "Sign up free"}</Button></Link>
                  </>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet> : (
          <button type="button" className="lg:hidden p-2 ml-auto" data-testid="nav-mobile-menu-button" aria-label="Open navigation menu">
            <Menu className="w-6 h-6 text-slate-800" />
          </button>
        )}
      </div>
      <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} />
    </header>
  );
}
